import React, { useEffect, useRef, useState } from 'react';

import {
  buscarAssociadosDoRecorte,
  buscarDetalheRadarRelacionamento,
  RadarRelacionamentoDetalhe,
  RecorteDoRelacionamento,
} from '../../../../api/contac/contacRecomendacoesApi';
import { formatarCelula } from '../../../../components/radar/formatarCelula';
import { notify } from '../../../../shared/feedback/notify';
import '../../../../shared/feedback/erro-de-tela.css';
import '../../../../shared/ui/modal-close-button.css';

interface Props {
  /** O cartão a detalhar. Ausente quando o modal abre um RECORTE de achado. */
  cardId?: string;
  /**
   * O recorte de um achado do agente (06/09/2026) — "de quais placas este número fala".
   *
   * Exatamente um dos dois (`cardId` ou `recorte`) é passado. O modal é o MESMO de propósito: as
   * duas listas são a mesma fila de cobrança, e um componente próprio para cada uma faria a
   * declaração da conferência, o aviso de corte e a placa clicável divergirem no primeiro ajuste.
   */
  recorte?: RecorteDoRelacionamento;
  titulo: string;
  /** O MESMO período do painel — divergir faria o cartão e a lista descreverem populações diferentes. */
  periodoInicio?: string;
  onClose: () => void;
  /** Leva a placa para a busca da tela, para o operador abrir o histórico dela. */
  onBuscarPlaca: (placa: string) => void;
}

/** A lista, sem o que identifica a origem dela — é o que o corpo do modal renderiza. */
type ListaExibivel = Omit<RadarRelacionamentoDetalhe, 'cardId'>;

/**
 * Os associados por trás de um cartão — ou de um achado — do Radar do Relacionamento (contac/at-13).
 *
 * Irmão do `DetalheRecomendacaoModal` do Radar da diretoria, com duas diferenças que vêm do papel:
 * a placa **não** abre a DRE (o RELACIONAMENTO não vê o detalhamento financeiro — ADR 0002), e sim
 * preenche a busca da tela; e nenhuma coluna é de dinheiro.
 */
const DetalheRelacionamentoModal: React.FC<Props> = ({
  cardId,
  recorte,
  titulo,
  periodoInicio,
  onClose,
  onBuscarPlaca,
}) => {
  const [detalhe, setDetalhe] = useState<ListaExibivel | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // A identidade da carga em texto: o objeto do recorte é recriado a cada render do pai e, como
  // dependência, refaria a consulta indefinidamente.
  const chaveDaCarga = cardId ?? JSON.stringify(recorte ?? {});

  useEffect(() => {
    let ativo = true;

    (async () => {
      try {
        const dados = cardId
          ? await buscarDetalheRadarRelacionamento(cardId, periodoInicio)
          : await buscarAssociadosDoRecorte(recorte ?? {}, periodoInicio);
        if (ativo) setDetalhe(dados);
      } catch (error) {
        if (!ativo) return;
        // Estado seguro + sinal: tabela vazia aqui contradiria o número que o usuário clicou.
        setDetalhe(null);
        setErro(true);
        notify.errorFrom(
          error,
          'Não foi possível carregar os associados desta lista. Recarregue a página; se persistir, acione a TI.',
        );
      } finally {
        if (ativo) setCarregando(false);
      }
    })();

    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveDaCarga, periodoInicio]);

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(evento.target as Node)) onClose();
    };
    const escape = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', escape);
    };
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="do-modal do-modal-largo" ref={modalRef}>
        <div className="do-modal-header">
          <span className="do-modal-titulo">{titulo}</span>
          <button type="button" className="close-button" onClick={onClose} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="do-modal-corpo">
          {carregando && <p className="do-kpi-carregando">Carregando os associados...</p>}

          {!carregando && erro && (
            <p className="erro-de-tela">
              Não foi possível carregar os associados. O número exibido continua válido; o que
              falhou foi a consulta desta lista.
            </p>
          )}

          {!carregando && !erro && detalhe && (
            <>
              {/*
                ⚠️ O corte é DECLARADO. A fila real chega a milhares de associados e a lista vem
                limitada: dizer "200 associados" sem avisar faria a tela afirmar que a fila
                inteira está ali — pior do que não listar, porque parece completo.
              */}
              <p className="do-modal-apoio">
                Situação em {detalhe.dataReferencia}.{' '}
                {detalhe.listaCortada ? (
                  <>
                    <strong>{detalhe.total.toLocaleString('pt-BR')} associados</strong> nesta
                    situação; a lista mostra os {detalhe.itens.length} de maior atraso.
                  </>
                ) : (
                  <>
                    {detalhe.total.toLocaleString('pt-BR')}{' '}
                    {detalhe.total === 1 ? 'associado' : 'associados'}.
                  </>
                )}{' '}
                Clique na placa para filtrar a lista da tela por ela.
              </p>

              {/*
                🔴 A conferência é DECLARADA, nos dois sentidos (ADR 0008). Quem já pagou saiu da
                lista — e dizer isso explica por que a lista tem menos linhas que o número do
                cartão. Quem não coubera no tempo permanece, e a tela avisa: fingir que a lista
                inteira foi confirmada é o erro que a conferência existe para não cometer.
              */}
              <p className="do-modal-apoio">
                {!detalhe.conferenciaDisponivel ? (
                  <>
                    A conferência em tempo real não está configurada neste ambiente: a lista mostra
                    a situação da última sincronização dos boletos.
                  </>
                ) : (
                  <>
                    {detalhe.removidosPorPagamento > 0 && (
                      <>
                        <strong>
                          {detalhe.removidosPorPagamento}{' '}
                          {detalhe.removidosPorPagamento === 1
                            ? 'associado já pagou e saiu da lista'
                            : 'associados já pagaram e saíram da lista'}
                        </strong>{' '}
                        na conferência feita agora.{' '}
                      </>
                    )}
                    {detalhe.conferidos > 0 && (
                      <>
                        {detalhe.conferidos}{' '}
                        {detalhe.conferidos === 1 ? 'linha conferida' : 'linhas conferidas'} direto
                        na Hinova.{' '}
                      </>
                    )}
                    {detalhe.naoConferidos > 0 && (
                      <>
                        {detalhe.naoConferidos}{' '}
                        {detalhe.naoConferidos === 1
                          ? 'não pôde ser conferida'
                          : 'não puderam ser conferidas'}{' '}
                        agora e {detalhe.naoConferidos === 1 ? 'mostra' : 'mostram'} a situação da
                        última sincronização.
                      </>
                    )}
                  </>
                )}
              </p>

              {detalhe.itens.length === 0 ? (
                <p className="do-modal-apoio">Nenhum associado nesta lista no momento.</p>
              ) : (
                <div className="do-tabela-wrapper">
                  <table className="do-tabela">
                    <thead>
                      <tr>
                        {detalhe.colunas.map((coluna) => (
                          <th key={coluna.chave}>{coluna.rotulo}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detalhe.itens.map((item, indice) => (
                        <tr key={indice}>
                          {detalhe.colunas.map((coluna) => {
                            const valor = formatarCelula(item[coluna.chave], coluna as any);

                            if (coluna.tipo === 'placa' && valor !== '-' && !valor.startsWith('Sem placa')) {
                              return (
                                <td key={coluna.chave} title={`Filtrar a lista por ${valor}`}>
                                  <button
                                    type="button"
                                    className="do-celula-link"
                                    onClick={() => {
                                      // Fecha antes de agir: o filtro muda a tela por baixo.
                                      onClose();
                                      onBuscarPlaca(valor);
                                    }}
                                  >
                                    {valor}
                                  </button>
                                </td>
                              );
                            }

                            return (
                              <td key={coluna.chave} title={valor}>
                                {valor}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetalheRelacionamentoModal;
